import * as Yup from 'yup';
import moment from 'moment';
import { checkDependencies } from '../../../utils/shemas';

const currentYear = moment().format('YYYY');

const defaultSchema = {
  userName: Yup.string()
    .min(2, 'schemas:personal-form.userName.min')
    .max(50, 'schemas:personal-form.userName.max')
    .nullable(),
  firstName: Yup.string()
    .min(2, 'schemas:personal-form.firstName.min')
    .max(50, 'schemas:personal-form.firstName.max')
    .nullable(),
  lastName: Yup.string()
    .min(2, 'schemas:personal-form.lastName.min')
    .max(50, 'schemas:personal-form.lastName.max')
    .nullable(),
  nationality: Yup.string(
    'schemas:personal-form.nationality.string'
  ).nullable(),
  city: Yup.string()
    .min(2, 'schemas:personal-form.city.min')
    .max(50, 'schemas:personal-form.city.max')
    .test(
      'validate-city',
      'schemas:personal-form.city.validate',
      function compareCity(city) {
        const zip = this.resolve(Yup.ref('zip'));
        const streetAndNumber = this.resolve(Yup.ref('streetAndNumber'));

        return checkDependencies(city, [zip, streetAndNumber]);
      }
    )
    .nullable(),
  zip: Yup.string()
    .nullable()
    .test(
      'validate-zip',
      'schemas:personal-form.zip.validate',
      function compareCity(zip) {
        const city = this.resolve(Yup.ref('city'));
        const streetAndNumber = this.resolve(Yup.ref('streetAndNumber'));

        return checkDependencies(zip, [city, streetAndNumber]);
      }
    ),
  streetAndNumber: Yup.string()
    .min(2, 'schemas:personal-form.streetAndNumber.min')
    .max(50, 'schemas:personal-form.streetAndNumber.max')
    .test(
      'validate-street',
      'schemas:personal-form.streetAndNumber.validate',
      function compareCity(streetAndNumber) {
        const zip = this.resolve(Yup.ref('zip'));
        const city = this.resolve(Yup.ref('city'));
        return checkDependencies(streetAndNumber, [zip, city]);
      }
    )
    .test(
      'validate-street-number',
      'schemas:personal-form.streetAndNumber.validateNumber',
      function checkStreetNumber(street) {
        if (street !== null && street !== undefined) {
          return /\d/.test(street);
        }

        return true;
      }
    )
    .nullable(),
  country: Yup.string('schemas:personal-form.country.string').nullable(),
  dayOfBirthday: Yup.number('schemas:personal-form.dayOfBirthday.number')
    .typeError('schemas:personal-form.dayOfBirthday.number')
    .min(1, 'schemas:personal-form.dayOfBirthday.min')
    .max(31, 'schemas:personal-form.dayOfBirthday.max')
    .test(
      'validate-month-year',
      'schemas:personal-form.dayOfBirthday.validate',
      function compareDay(day) {
        const month = this.resolve(Yup.ref('monthOfBirthday'));
        const year = this.resolve(Yup.ref('yearOfBirthday'));
        return checkDependencies(day, [month, year]);
      }
    )
    .nullable(),
  monthOfBirthday: Yup.number('schemas:personal-form.monthOfBirthday.number')
    .typeError('schemas:personal-form.monthOfBirthday.number')
    .min(1, 'schemas:personal-form.monthOfBirthday.min')
    .max(12, 'schemas:personal-form.monthOfBirthday.max')
    .test(
      'validate-day-year',
      'schemas:personal-form.monthOfBirthday.validate',
      function compareMonth(month) {
        const day = this.resolve(Yup.ref('dayOfBirthday'));
        const year = this.resolve(Yup.ref('yearOfBirthday'));
        return checkDependencies(month, [day, year]);
      }
    )
    .nullable(),
  yearOfBirthday: Yup.number('schemas:personal-form.yearOfBirthday.number')
    .typeError('schemas:personal-form.yearOfBirthday.number')
    .min(1900, 'schemas:personal-form.yearOfBirthday.min')
    .max(currentYear - 18, 'schemas:personal-form.yearOfBirthday.max')
    .test(
      'validate-day-month',
      'schemas:personal-form.yearOfBirthday.validate',
      function compareYear(year) {
        const day = this.resolve(Yup.ref('dayOfBirthday'));
        const month = this.resolve(Yup.ref('monthOfBirthday'));
        return checkDependencies(year, [day, month]);
      }
    )
    .nullable()
};

function generateValidationScheme(fields = {}) {
  const validation = {};
  const keys = Object.keys(fields);

  keys.forEach(key => {
    if (fields[key] !== null) {
      let field = null;

      switch (fields[key].type) {
        case 'string':
          field = Yup.string();
          break;
        case 'number':
          field = Yup.number();
          break;
        default:
          field = Yup.mixed();
          break;
      }

      if (fields[key].required) {
        field = field.required();
      } else {
        field = field.nullable();
      }
      validation[key] = field;
    }
  });

  return Yup.object().shape({
    ...defaultSchema,
    ...validation
  });
}

export default generateValidationScheme;
